import React, { memo, useEffect, useMemo } from 'react';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { useQueryBasedViewAutomaticTracking } from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { ff } from '@atlassian/jira-feature-flagging-using-meta';
import { ErrorFlag } from '@atlassian/jira-issue-error-flag';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { VIEW_EXPERIENCE } from '../../../common/constants';
import { useListViewIssues as useListViewIssuesService } from '../../../services/get-list-view-issues';
import { useHierarchyLevel } from '../../../services/hierarchy-level';
import { useIssueFields } from '../../get-issue-fields';
import { useHierarchyConfig } from '../hierarchy-config';
import { useHierarchyStore } from '../hierarchy-store';
import { useResetItems } from '../reset-items';
import { useSetHierarchyData } from '../set-hierarchy-data';
import messages from './messages';

export const useGetHierarchyItems = () => {
	const [{ hierarchyLevel }, { resetHierarchyLevel, setHierarchyLevel }] = useHierarchyLevel();
	const { jqlContext, quickFiltersJql, sorting, isHidingDoneItems, jqlErrors } =
		useFilteredViewContextInput({
			view: 'list',
		});
	const { showFlatList, hierarchyParent } = useHierarchyConfig();
	const prevShowFlatList = usePreviousWithInitial(showFlatList);
	const {
		hierarchyLevelsWithCache,
		getQueryFieldIds,
		isParentFieldVisible,
		isDevSummaryFieldVisible,
	} = useIssueFields();
	const queryFieldIds = getQueryFieldIds();
	const isValidFiltersJql = !jqlErrors.length;

	let projectKey: string | undefined;
	if (ff('list-uses-new-jql-parent-project-directive_v5gfs')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const projectData = useProject();

		projectKey = projectData.key;
	}

	// fetch issues based on `hierarchyLevel` - regardless of issue types
	const items = useListViewIssuesService({
		input: {
			jqlContext,
			quickFiltersJql,
			fieldIds: queryFieldIds,
			sorting,
			hierarchyLevel,
			hierarchyParent,
			isHidingDoneItems,
			projectIdOrKey: projectKey,
			isParentFieldVisible: isParentFieldVisible(),
			isDevSummaryFieldVisible: isDevSummaryFieldVisible(),
		},
		condition: typeof hierarchyLevel === 'number' && queryFieldIds.length > 0 && isValidFiltersJql,
	});

	useEffect(() => {
		if (hierarchyLevel == null && hierarchyLevelsWithCache.length) {
			setHierarchyLevel(hierarchyLevelsWithCache[0].level);
		}
	}, [hierarchyLevel, hierarchyLevelsWithCache, setHierarchyLevel]);

	// reset hierarchyLevel to the top level when filtering change
	useEffect(() => {
		if (hierarchyLevelsWithCache.length && prevShowFlatList && !showFlatList) {
			setHierarchyLevel(hierarchyLevelsWithCache[0].level);
		}
	}, [showFlatList, prevShowFlatList, setHierarchyLevel, hierarchyLevelsWithCache]);

	// reset hierarchyLevel on unmount
	useEffect(() => () => resetHierarchyLevel(), []); // eslint-disable-line react-hooks/exhaustive-deps

	const levels = useMemo(
		() => [...new Set(hierarchyLevelsWithCache.map((x) => x.level))],
		[hierarchyLevelsWithCache],
	);

	// When BE service returns empty response from top level items query
	// We take next hierarchy level and re-fetch items... until BE returs some data, or we reach the bottom hierarchy level
	useEffect(() => {
		if (!items.loading && !items.data.length && hierarchyLevel != null) {
			const nextIndex = levels.indexOf(hierarchyLevel) + 1;
			if (typeof levels[nextIndex] === 'number') {
				setHierarchyLevel(levels[nextIndex]);
			}
		}
	}, [hierarchyLevel, levels, items.data.length, items.loading, setHierarchyLevel]);

	useEffect(() => {
		if (jqlErrors.length) {
			fireErrorAnalytics({
				meta: {
					id: 'listHierarchyLoader',
					packageName: 'jiraBusinessList',
					teamName: 'keystone',
				},
				attributes: {
					message: 'Error in the JQL query',
				},
				error: jqlErrors[0],
				skipSentry: true,
			});
		}
	}, [jqlErrors]);

	return useMemo(
		() => ({
			...items,
			loading: isValidFiltersJql ? items.loading : false,
		}),
		[items, isValidFiltersJql],
	);
};

const HierarchyLoader = () => {
	const items = useGetHierarchyItems();
	const prevItems = usePreviousWithInitial(items);
	const [, { resetCache }] = useHierarchyStore();
	const { setHierarchyData } = useSetHierarchyData();

	useQueryBasedViewAutomaticTracking(VIEW_EXPERIENCE, {
		loading: items.loading,
		error: items.error,
	});

	// resets expanded items and queryData cache when filtering/sorting/groupBy is updated
	useResetItems();

	// initialize items when switching between grouping -> hierarchy
	useEffect(() => {
		setHierarchyData(items);
		resetCache();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// updates hierarchy items in store only when "data", "isLoadingNextPage" or "totalCount" changes
	useEffect(() => {
		if (prevItems.data !== items.data || prevItems.isLoadingNextPage !== items.isLoadingNextPage) {
			setHierarchyData(items);
		}
	}, [items, prevItems, setHierarchyData]);

	return (
		<>
			{items.error && (
				<ErrorFlag
					error={items.error}
					title={messages.errorTitle}
					description={messages.errorMessage}
				/>
			)}
		</>
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default memo<Record<any, any>>(HierarchyLoader);
